import { useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  Alert,
  Button,
  Input,
  RadioGroup,
  Spinner,
  cn,
  useDialogFocus,
  useModalPresence,
  type OrgType,
  type PartnerMembershipOut,
  type PartnerOrgCreateRequest,
} from "@dearbill/shared";
import { useApi } from "../auth/useApi";

const ORG_TYPE_OPTIONS: { value: OrgType; label: string }[] = [
  { value: "Provider", label: "Provider" },
  { value: "Pharmacy", label: "Pharmacy" },
  { value: "Broker", label: "Broker" },
];

/**
 * Portal-mounted "New organization" dialog for account owners. The owner enters
 * an org name, picks its type and submits; on success the parent appends the
 * returned `PartnerMembershipOut` to `me.memberships` and closes the dialog.
 *
 * Mechanics mirror the customer app's `EnrollSuccessModal`: `createPortal` to
 * `document.body`, `useDialogFocus` for the scroll lock, focus and Tab wrap
 * while `open`, `useModalPresence` for the open and close motion, and
 * `role="dialog"`, `aria-modal` and `aria-labelledby`. The partner SPA has no
 * i18n, so all copy is plain strings. A server failure shows in a standalone
 * `role="alert"` region rather than the field's `error` prop, which would mark
 * the input invalid.
 *
 * Only the Cancel button dismisses it; there is no backdrop or Escape
 * dismissal. On success it calls only `onCreated`, and the parent closes it,
 * so `toggle` never fires twice. The typed fields live in this component, so
 * a caller that wants them blank on each opening keys it per opening.
 */
export interface NewOrgModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (membership: PartnerMembershipOut) => void;
}

export function NewOrgModal({ open, onClose, onCreated }: NewOrgModalProps): ReactNode {
  const api = useApi();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const { mounted, closing, onAnimationEnd } = useModalPresence(open);
  useDialogFocus({ open, panelRef });
  const [name, setName] = useState("");
  const [organizationType, setOrganizationType] = useState<OrgType | "">("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!mounted) return null;

  const trimmed = name.trim();
  const canSubmit = !pending && trimmed.length > 0 && organizationType !== "";

  // A synchronous void handler, like MembersTab's onInvite, so the click
  // attribute isn't handed a promise; it starts the POST and branches in then/catch.
  const handleSubmit = (): void => {
    // `canSubmit` includes `organizationType !== ""`, so TS narrows the union to
    // `OrgType` below (aliased-condition narrowing) for the `satisfies` check.
    if (!canSubmit || !open) return;
    setPending(true);
    setError(null);
    api<PartnerMembershipOut>("/api/partner/orgs", {
      method: "POST",
      body: { name: trimmed, organizationType } satisfies PartnerOrgCreateRequest,
    })
      .then((membership) => {
        // Hand the new membership up. The parent appends it and closes the
        // dialog; calling onClose here as well would toggle twice.
        onCreated(membership);
      })
      .catch(() => {
        // An ApiError (403 non-owner, 422 blank, 5xx) or a network failure. The
        // owner-only button gate prevents the 403 in practice. Any failure keeps
        // the modal open with an inline message so the owner can retry.
        setError("Couldn't create the organization. Please try again.");
        setPending(false);
      });
  };

  const node = (
    <div
      className={cn(
        "bg-midnight/30 fixed inset-0 z-50 flex items-center justify-center px-4 py-6",
        open ? "motion-safe:animate-modal-backdrop-in" : "motion-safe:animate-modal-backdrop-out",
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      inert={closing}
      aria-hidden={closing || undefined}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          "w-full max-w-md rounded-3xl bg-white p-6 shadow-xl",
          open ? "motion-safe:animate-modal-panel-in" : "motion-safe:animate-modal-panel-out",
        )}
        onAnimationEnd={onAnimationEnd}
      >
        <h2 id={titleId} className="text-midnight font-serif text-xl font-normal leading-[1.5]">
          New organization
        </h2>
        <p className="text-slate-teal mt-1 text-sm">
          Name your organization and choose its type. You can complete the rest of its profile
          afterwards.
        </p>

        <div className="mt-5 space-y-5">
          <Input
            label="Organization name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={pending}
          />
          <RadioGroup
            legend="Organization type"
            required
            name="organizationType"
            options={ORG_TYPE_OPTIONS}
            value={organizationType}
            disabled={pending}
            onChange={(e) => setOrganizationType(e.target.value as OrgType)}
          />
        </div>

        {error && <Alert tone="error" title={error} className="mt-4" />}

        <div className="mt-6 flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              if (open) onClose();
            }}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {pending ? (
              <>
                <Spinner size="sm" className="mr-2" /> Creating…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
